import { css } from '@emotion/css';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { throttle } from 'src/lib/utils';
import { AppIconImage } from './AppIconImage';
import { AppIconImageContainer } from './AppIconImageContainer';

export const featureTypes = [
  'passport',
  'chat',
  'best-friends',
  'reaction',
  'catalog',
  'fortune',
  'resort',
  'newspaper',
  'life-supports',
  'my-design',
  'settings'
] as const;

export type FeatureType = typeof featureTypes[number];

interface AppIconProps {
  feature: FeatureType;
}

export const AppIcon: React.FC<AppIconProps> = ({ feature }) => {
  const { t } = useTranslation('translation');
  const history = useHistory();
  const data = getIconData(feature);
  const handleClick = useCallback(
    throttle(
      () => {
        if (data) {
          history.push(data.path);
        }
      },
      1000,
      200
    ),
    [data, history]
  );

  if (!data) {
    return null;
  }

  const titleText = t(data.textId);
  const labelId = `feature-ic-${feature}`;

  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 33.33333%;
        height: auto;
        max-height: 40vw;
        padding: 8px;
        button {
          flex: 0 0 auto;
          width: 100%;
          border-radius: 36%;
          background: ${data.color};
          margin: 0;
          padding: 0;
          &,
          svg {
            transition: transform 0.1s;
            transform: scale(1);
          }
          &:active {
            transform: scale(0.88);
          }
          &:active svg {
            transform: scale(0.9);
          }
        }
        /* 画面の縦横比が小さい端末でスクロールが発生するのを抑制するために画面高さをもとに最大幅を決定します。 */
        @media (max-height: 544px) {
          button {
            max-width: 14vh;
          }
        }
      `}
    >
      <button aria-labelledby={labelId} onClick={handleClick}>
        <AppIconImageContainer>
          <AppIconImage feature={feature} width="100%" height="100%" />
        </AppIconImageContainer>
      </button>
      <label
        id={labelId}
        aria-hidden="true"
        className={css`
          flex: 0 0 auto;
          overflow: hidden;
          font-size: 10px;
          max-width: 104px;
          white-space: nowrap;
          text-overflow: ellipsis;
          margin: 6px 0 0;
          padding: 0 4px;
          @media (max-width: 320px), (max-height: 580px) {
            max-width: 88px;
          }
        `}
      >
        {titleText}
      </label>
    </div>
  );
};

function getIconData(
  feature: FeatureType
): { textId: string; color: string; path: string } | undefined {
  switch (feature) {
    case 'passport': {
      const textId = 'passport';
      const color = '#87c589';
      const path = '/players/passport';
      return { textId, color, path };
    }
    case 'my-design': {
      const textId = 'my_design';
      const color = '#f8a6b1';
      const path = '/players/my-design';
      return { textId, color, path };
    }
    case 'chat': {
      const textId = 'chat';
      const color = '#ced936';
      const path = '/players/chat';
      return { textId, color, path };
    }
    case 'best-friends': {
      const textId = 'best_friends';
      const color = '#f88d62';
      const path = '/players/best-friends';
      return { textId, color, path };
    }
    case 'reaction': {
      const textId = 'reaction';
      const color = '#ce9ad6';
      const path = '/players/reaction';
      return { textId, color, path };
    }
    case 'catalog': {
      const textId = 'catalog';
      const color = '#ebdc55';
      const path = '/players/catalog/home';
      return { textId, color, path };
    }
    case 'fortune': {
      const textId = 'fortune';
      const color = '#c1eb76';
      const path = '/players/fortune/home';
      return { textId, color, path };
    }
    case 'resort': {
      const textId = 'resort';
      const color = '#d15e5e';
      const path = '/players/resort/home';
      return { textId, color, path };
    }
    case 'life-supports': {
      const textId = 'life_supports';
      const color = '#9083cc';
      const path = '/players/life-supports';
      return { textId, color, path };
    }
    case 'newspaper': {
      const textId = 'newspaper';
      const color = '#faa705';
      const path = '/players/newspaper/home';
      return { textId, color, path };
    }
    case 'settings': {
      const textId = 'settings';
      const color = '#c0a971';
      const path = '/players/settings';
      return { textId, color, path };
    }
    default:
      return;
  }
}
